import { ApolloClient, ApolloError } from '@apollo/client'
import { bind } from '@react-rxjs/core'
import { createKeyedSignal } from '@react-rxjs/utils'
import {
  catchError,
  combineLatest,
  defer,
  EMPTY,
  map,
  of,
  retry,
  switchMap,
  take,
  timer,
} from 'rxjs'

import {
  SearchProductBySkuDocument,
  SearchProductBySkuQueryVariables,
} from '../../../../graphql/search'
import { logAndCaptureException } from '../../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../../utils/rx/errors'
import { latestAccessToken$ } from '../../../auth/state'
import { latestApolloClient$ } from '../../../graphql/apollo'

export const [cartItemSku$, setCartItemSku] = createKeyedSignal<string>()

const cartItemProductFactory = (
  client: ApolloClient<unknown>,
  token: string | undefined,
  variables: SearchProductBySkuQueryVariables,
) =>
  defer(() =>
    client.query({
      query: SearchProductBySkuDocument,
      variables,
      context: { token, uri: process.env.GATSBY_SEARCH_URL },
      // don't throw on GraphQL errors
      errorPolicy: 'all',
    }),
  ).pipe(
    // retry network errors
    retry({
      count: 3,
      delay: (_, count) =>
        // retry with exponential backoff and max of 60 seconds
        timer(Math.min(60000, 2 ^ (count * 1000))),
    }),
    map(({ data, errors }) => {
      if (errors?.length) {
        // log GraphQL errors and return left
        const error = new ApolloError({ graphQLErrors: errors })
        logAndCaptureException(error)
        return {
          _tag: 'Left' as const,
          error,
        }
      }
      return {
        _tag: 'Right' as const,
        data,
      }
    }),
    catchError(coerceCaughtToLeft),
  )

const cartItemProduct$ = (sku: string) =>
  combineLatest([latestApolloClient$.pipe(take(1)), latestAccessToken$, cartItemSku$(sku)]).pipe(
    switchMap(([client, token]) =>
      cartItemProductFactory(client, token, { sku }).pipe(
        switchMap((result) => {
          // not much we can do if the dispensary cannot be resolved
          if (result._tag === 'Left') {
            return EMPTY
          }
          const product = result.data.productBySku
          // return EMPTY if dispensary not found for practitioner code
          return !product ? EMPTY : of(product)
        }),
      ),
    ),
  )

export const [useCartItemProduct, latestCartItemProduct$] = bind(cartItemProduct$)

export const [useCartItemProducts, latestCartItemProducts$] = bind((skus: string[]) =>
  skus.length > 0 ? combineLatest(skus.map((sku) => cartItemProduct$(sku))) : of([]),
)
